import { ReactNode, useMemo } from "react";
import clsx from "clsx";
// React Redux
import { useSelector } from "react-redux";

// React Router
import { Link } from "react-router-dom";

// Types
import { RootState } from "@/redux/store";

// MUI
import { Box, Card, CardActionArea, Divider, Grid, Skeleton, SvgIcon, Typography } from "@mui/material";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useManagedPositions } from "@/Common/hooks/Positions";

// Icons
import { ReactComponent as CallActivityIcon } from "@/assets/icons/dashboard/MyIcons/CallActivity.svg";
import { ReactComponent as CITIcon } from "@/assets/icons/dashboard/MyIcons/CIT.svg";
import { ReactComponent as ExclamationIcon } from "@/assets/icons/dashboard/exclamation.svg";
import { ReactComponent as IncentiveIcon } from "@/assets/icons/dashboard/MyIcons/Motivators.svg";
import { ReactComponent as OEMIcon } from "@/assets/icons/dashboard/MyIcons/OEM.svg";
import { ReactComponent as PayPlansIcon } from "@/assets/icons/dashboard/MyIcons/PayPlans.svg";
import { ReactComponent as SupportIcon } from "@/assets/icons/dashboard/MyIcons/Support.svg";
import { ReactComponent as TrainingIcon } from "@/assets/icons/dashboard/MyIcons/Training.svg";
import { ReactComponent as WashoutIcon } from "@/assets/icons/dashboard/MyIcons/Washouts.svg";

// Constants
import actions from "@/Constants/actionsList";

//****************************************** HELPERS ***************************************************** //
const isActionRequired = (actionRequiredList: IMyRTCProps["actionRequiredList"] = [], actionName = "") => {
	if (actionRequiredList?.length) {
		return actionRequiredList.some((action) => action.component === actionName);
	}
	return false;
};

interface IMyRTCProps {
	actionRequiredList?: { component: string }[];
}

const isFIManager = (position = "") => position === "F&I Manager";

export default function MyRTC(props: Readonly<IMyRTCProps>) {
	const { useAuth: isAuthorized } = useAuthorization();
	const user = useSelector((state: RootState) => state.user.user);
	const washouts = isAuthorized(actions.VIEW_MY_WASHOUTS);
	const payPlans = isAuthorized(actions.VIEW_MY_PAY_PLANS);
	const motivators = isAuthorized(actions.VIEW_MY_MOTIVATORS);
	const oem = isAuthorized(actions.VIEW_MY_CUSTOMER_SATISFACTION_SCORES);
	const cit = isAuthorized(actions.VIEW_MY_CIT_REPORTS);
	const callActivity = isAuthorized(actions.VIEW_MY_CALL_ACTIVITY);
	const support = isAuthorized(actions.VIEW_COMMISSION_SUPPORT);

	const { matchPosition, isPending } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const positions = matchPosition("Sales Associate");

	const options = useMemo(
		() => [
			{
				title: "My Washouts",
				icon: (
					<SvgIcon
						component={WashoutIcon}
						inheritViewBox
						className="text-transparent !text-[1.7rem]"
						aria-label="My Washouts Icon"
					/>
				),
				link: "/washouts/personal",
				enabled: washouts,
				show: true,
				actionRequired: isActionRequired(props.actionRequiredList, "My Washouts"),
			},
			{
				title: "My Pay Plans",
				icon: (
					<SvgIcon
						component={PayPlansIcon}
						inheritViewBox
						className="text-transparent !text-[1.7rem]"
						aria-label="My Pay Plan icon"
					/>
				),
				link: "/pay-plans/personal",
				enabled: payPlans && !isFIManager(user?.position),
				show: true,
				actionRequired: isActionRequired(props.actionRequiredList, "My Pay Plans"),
			},
			{
				title: "My Motivators",
				icon: (
					<SvgIcon
						component={IncentiveIcon}
						inheritViewBox
						className="text-transparent !text-[1.7rem]"
						aria-label="My Motivators icon"
					/>
				),
				link: "/my-motivators",
				enabled: motivators && !isFIManager(user?.position),
				show: true,
				actionRequired: isActionRequired(props.actionRequiredList, "My Motivators"),
			},
			{
				title: "My Training",
				icon: (
					<SvgIcon
						component={TrainingIcon}
						inheritViewBox
						className="text-transparent !text-[1.7rem]"
						aria-label="My Training icon"
					/>
				),
				link: "/",
				enabled: false,
				show: true,
				actionRequired: isActionRequired(props.actionRequiredList, "Access My Training"),
			},
			{
				title: "My OEM",
				icon: <SvgIcon component={OEMIcon} inheritViewBox className="text-transparent !text-[1.7rem]" aria-label="My OEM Icon" />,
				link: "/my-oem-data",
				enabled: oem,
				show: isPending ? true : positions?.includes(user?.position ?? "") ?? false,
				isLoading: isPending,
				actionRequired: isActionRequired(props.actionRequiredList, "My OEM"),
			},
			{
				title: "My Call Activity",
				icon: (
					<SvgIcon
						component={CallActivityIcon}
						inheritViewBox
						className="text-transparent !text-[1.7rem]"
						aria-label="My Call Activity Icon"
					/>
				),
				link: "/my-call-activity",
				enabled: callActivity,
				show: true,
				actionRequired: isActionRequired(props.actionRequiredList, "My Call Activity"),
			},
			{
				title: "My CIT",
				icon: <SvgIcon component={CITIcon} inheritViewBox className="text-transparent !text-[1.7rem]" aria-label="My CIT Icon" />,
				link: "/my-cit",
				enabled: cit,
				show: isFIManager(user?.position),
				actionRequired: isActionRequired(props.actionRequiredList, "My CIT"),
			},
			{
				title: "My Comm. Support",
				icon: (
					<SvgIcon
						component={SupportIcon}
						inheritViewBox
						className="text-transparent !text-[1.8rem]"
						aria-label="My Comm. Support"
					/>
				),
				link: "/support-requests",
				enabled: support,
				show: true,
				actionRequired: isActionRequired(props.actionRequiredList, "My Support Center"),
			},
		],
		[props, user, isPending, positions, washouts, payPlans, motivators, oem, cit, callActivity]
	);

	return (
		<>
			<Divider className="!mb-4" />
			<Grid container spacing={1}>
				{options.map((option, key) => {
					return <MyRTCOption key={`${option?.title}_${key}`} {...option} />;
				})}
			</Grid>
		</>
	);
}

interface IMyRTCOptionProps {
	title: string;
	icon: ReactNode;
	link: string;
	enabled: boolean;
	isLoading?: boolean;
	actionRequired: boolean;
	show: boolean;
}

const MyRTCOption = (props: Readonly<IMyRTCOptionProps>) => {
	if (!props.show) {
		return null;
	}

	return (
		<Grid key={props.link} item xs={4} position="relative">
			<Card
				elevation={0}
				className={clsx("!rounded-[10px] h-20 !bg-transparent !block border", {
					"cursor-pointer": props.enabled && !props.isLoading,
					"!cursor-not-allowed pointer-events-none": !(props.enabled && !props.isLoading),
					"border-alert border-2": props.actionRequired && !props.isLoading,
					"border-shadow-gray border-[1px]": !(props.actionRequired && !props.isLoading),
				})}
				component={Link}
				to={props.link || "/"}
			>
				{props.actionRequired && !props.isLoading && (
					<Box className="absolute top-px right-5">
						<ExclamationIcon aria-label="attention-icon" height="14px" />
					</Box>
				)}

				{props.isLoading ? (
					<Skeleton variant="rectangular" className="!h-full" />
				) : (
					<CardActionArea
						disabled={!props.enabled}
						className={clsx("!flex flex-col justify-center items-center text-center h-full", {
							"opacity-50": !props.enabled,
						})}
					>
						<Box className="overflow-y-hidden h-8 flex items-center">{props.icon}</Box>
						<Typography className="!font-sans !font-semibold text-default !text-[14px] !mt-[0.2rem]" component="p">
							{props.title}
						</Typography>
					</CardActionArea>
				)}
			</Card>
		</Grid>
	);
};
